// src/components/client/ServiceSummary.tsx
import React from 'react';
import { format, parseISO } from 'date-fns';
import { formatCurrency } from '../../utils/formatters';
import { clampDownPaymentPercent } from '../../utils/downPaymentDisplay';

interface ServiceSummaryProps {
  service: {
    name: string;
    price: number;
    duration: number;
    description?: string;
  };
  stylist: {
    name: string;
    profilePhoto?: string;
  };
  date: Date;
  time: {
    startTime: string;
  };
  /** @deprecated use loyaltyDiscount + promoDiscount instead */
  discount?: number;
  loyaltyDiscount?: number;
  promoDiscount?: number;
  promoTitle?: string;
  addOnsTotal?: number;
  finalPrice: number;
  showDownPayment?: boolean;
  /** Stylist's resolved down payment percentage (e.g. 50). 0 means off / none. */
  downPaymentPercentage?: number;
}

const ServiceSummary: React.FC<ServiceSummaryProps> = ({
  service,
  stylist,
  date,
  time,
  discount: legacyDiscount,
  loyaltyDiscount = 0,
  promoDiscount = 0,
  promoTitle,
  addOnsTotal = 0,
  finalPrice,
  showDownPayment = true,
  downPaymentPercentage = 0
}) => {
  const totalDiscount = loyaltyDiscount + promoDiscount || legacyDiscount || 0;
  // Format the time for display
  const formatTime = (isoTime: string): string => {
    try {
      const timeDate = parseISO(isoTime);
      return format(timeDate, 'h:mm a');
    } catch (error) {
      console.error('Error parsing time:', error);
      return isoTime;
    }
  };

  const percent = clampDownPaymentPercent(downPaymentPercentage);
  const downPaymentAmount = finalPrice * (percent / 100);
  const remainingAmount = finalPrice;
  
  return (
    <div className="booking-summary">
      <h3 className="booking-summary__title">
        Appointment Summary
      </h3>
      
      <div className="booking-summary__grid">
        <div className="booking-summary__column">
          <div className="booking-summary__detail">
            <svg className="booking-summary__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            <span className="booking-summary__text">
              {format(date, 'EEEE, MMMM d, yyyy')}
            </span>
          </div>
          
          <div className="booking-summary__detail">
            <svg className="booking-summary__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
            <span className="booking-summary__text">
              {formatTime(time.startTime)}
            </span>
          </div>
          
          <div className="booking-summary__detail">
            <svg className="booking-summary__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
            <span className="booking-summary__text">
              {stylist.name}
            </span>
          </div>
        </div>
        
        <div className="booking-summary__column">
          <div className="booking-summary__service">
            <div className="booking-summary__service-image">
              {stylist.profilePhoto ? (
                <img src={stylist.profilePhoto} alt={stylist.name} />
              ) : (
                <div className="booking-summary__service-image-initials" aria-label={stylist.name}>
                  {stylist.name?.charAt(0).toUpperCase() ?? '?'}
                </div>
              )}
            </div>
            <div className="booking-summary__service-info">
              <div className="booking-summary__service-name">
                {service.name}
              </div>
              <div className="booking-summary__service-duration">
                {service.duration} minutes
              </div>
            </div>
          </div>
          
          {service.description && (
            <p className="booking-summary__description">
              {service.description}
            </p>
          )}
        </div>
      </div>
      
      <div className="booking-summary__divider"></div>
      
      <div className="booking-summary__pricing">
        <div className="booking-summary__price-row">
          <span className="booking-summary__price-label">Service Price:</span>
          <span className="booking-summary__price-value">{formatCurrency(service.price)}</span>
        </div>

        {addOnsTotal > 0 && (
          <div className="booking-summary__price-row">
            <span className="booking-summary__price-label">Add-ons:</span>
            <span className="booking-summary__price-value">+{formatCurrency(addOnsTotal)}</span>
          </div>
        )}

        {promoDiscount > 0 && (
          <div className="booking-summary__price-row">
            <span className="booking-summary__price-label">🎁 {promoTitle || 'Promo'}:</span>
            <span className="booking-summary__price-value booking-summary__price-value--discount">
              -{formatCurrency(promoDiscount)}
            </span>
          </div>
        )}

        {loyaltyDiscount > 0 && (
          <div className="booking-summary__price-row">
            <span className="booking-summary__price-label">⭐ Loyalty Discount:</span>
            <span className="booking-summary__price-value booking-summary__price-value--discount">
              -{formatCurrency(loyaltyDiscount)}
            </span>
          </div>
        )}

        {/* Legacy fallback */}
        {totalDiscount > 0 && promoDiscount === 0 && loyaltyDiscount === 0 && legacyDiscount && legacyDiscount > 0 && (
          <div className="booking-summary__price-row">
            <span className="booking-summary__price-label">Discount:</span>
            <span className="booking-summary__price-value booking-summary__price-value--discount">
              -{formatCurrency(legacyDiscount)}
            </span>
          </div>
        )}
        
        <div className="booking-summary__total-row">
          <span className="booking-summary__total-label">Total:</span>
          <span className={`booking-summary__total-value ${totalDiscount > 0 ? 'booking-summary__total-value--discounted' : ''}`}>
            {formatCurrency(finalPrice)}
          </span>
        </div>
        
        {/* Down payment / full amount at completion */}
        {showDownPayment && (
          <>
            <div className="booking-summary__divider booking-summary__divider--dotted"></div>
            <div className="booking-summary__payment-info">
              {percent > 0 ? (
                <>
                  <div className="booking-summary__notice">
                    <svg className="booking-summary__info-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="16" x2="12" y2="12"></line>
                      <line x1="12" y1="8" x2="12.01" y2="8"></line>
                    </svg>
                    <span>A {percent}% booking guarantee is quoted and charged only if you cancel late or no-show. The full amount is charged when the appointment is completed.</span>
                  </div>
                  <div className="booking-summary__price-row booking-summary__price-row--highlight">
                    <span className="booking-summary__price-label">Quoted booking guarantee ({percent}%):</span>
                    <span className="booking-summary__price-value booking-summary__price-value--primary">
                      {formatCurrency(downPaymentAmount)}
                    </span>
                  </div>
                  <div className="booking-summary__price-row booking-summary__price-row--muted">
                    <span className="booking-summary__price-label">Charged at completion:</span>
                    <span className="booking-summary__price-value">
                      {formatCurrency(remainingAmount)}
                    </span>
                  </div>
                  <p className="booking-summary__payment-note">
                    The full amount is collected when your appointment is completed.
                  </p>
                </>
              ) : (
                <>
                  <div className="booking-summary__notice">
                    <svg className="booking-summary__info-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="16" x2="12" y2="12"></line>
                      <line x1="12" y1="8" x2="12.01" y2="8"></line>
                    </svg>
                    <span>No deposit required. Full amount due when your appointment is completed.</span>
                  </div>
                  <div className="booking-summary__price-row booking-summary__price-row--muted">
                    <span className="booking-summary__price-label">Charged at completion:</span>
                    <span className="booking-summary__price-value">
                      {formatCurrency(finalPrice)}
                    </span>
                  </div>
                  <p className="booking-summary__payment-note">
                    The full amount will be collected when your appointment is completed.
                  </p>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ServiceSummary;